import { CycleUtils } from '@wix/package-picker-shared';
import React from 'react';
import { useCurrentModule } from '../components/ModuleProvider';
import { COUNTRY_US, MOBILE_STICKY_BUTTON_SPEC } from '../utils/consts';
import { eclipseServerData } from '../utils/eclipseServerData';
import { isMobileStickyPlanAndCycleEnabled } from '../utils/isMobileStickyPlanAndCycleEnabled';
import { ECLIPSE_MOBILE_MODULE_NAME } from '../utils/moduleNames';
import { availableCyclesFromPricingDetails, clampToAvailableCycle, productFamilyFromId, recommendedCycleToPurchaseCycle } from './cycleRecommendation.utils';
import { resolveCurrentDefaultCycle } from './resolveCurrentDefaultCycle';
import { useCycleAvailability } from './useCycleAvailability';
import { useEclipseCycleRecommendation } from './useEclipseCycleRecommendation';
import { upgradeStateOf, useUpgradeRecommendation } from './useUpgradeRecommendation';
export const useCycleDefaultDecision = () => {
  var _eclipseServerData$ge;
  const module = useCurrentModule();
  const selectedProductId = module.SDK.PackagePickerStore.useSelectedProductId();
  const {
    data: discountData,
    isLoading: isDiscountLoading
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});
  const {
    data: product,
    isLoading: isProductLoading
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({
    productId: selectedProductId ?? ''
  }, Boolean(selectedProductId));
  const {
    cycleByFamily,
    shadowCycleByFamily,
    isShadowPending,
    correlationId,
    isEligible,
    isShadow,
    ineligibleReasons,
    isError,
    isTimeout,
    isLoading: isRecommendationLoading
  } = useEclipseCycleRecommendation();
  const upgrade = useUpgradeRecommendation();
  const isUpgradeModel = upgrade.isEligible && upgrade.entry === 'upgrade';
  const isUpgradeServing = isUpgradeModel && upgrade.variant === 'B';
  const upgradeResponse = upgrade.data ?? upgrade.shadowData;
  const {
    experiments
  } = module.internals.experiments.hooks.useExperiments();
  const isMobile = module.moduleName === ECLIPSE_MOBILE_MODULE_NAME;
  const isStickyButtonYearly = isMobile && experiments.isEnabled(MOBILE_STICKY_BUTTON_SPEC);
  const isStickyPlanAndCycle = isMobileStickyPlanAndCycleEnabled({
    experiments,
    isMobile
  });
  const isUsGeo = ((_eclipseServerData$ge = eclipseServerData.get().geo) == null ? void 0 : _eclipseServerData$ge.toUpperCase()) === COUNTRY_US;
  const isProductPending = Boolean(selectedProductId) && isProductLoading;
  const productFamily = productFamilyFromId(product == null ? void 0 : product.productFamilyId);
  const upgradeCardCycle = response => product != null && product.productFamilyId ? response == null ? void 0 : response.cycles.byProductFamilyId[product.productFamilyId] : undefined;
  const initialRecommendedCycle = isEligible ? productFamily && cycleByFamily[productFamily] : undefined;
  const appliedRecommendedCycle = isUpgradeModel ? upgradeCardCycle(upgrade.data) : initialRecommendedCycle;
  const recommendedPurchaseCycle = recommendedCycleToPurchaseCycle(appliedRecommendedCycle || undefined);
  const shadowRecommendedCycle = isUpgradeModel ? upgradeCardCycle(upgrade.shadowData) : isShadow ? productFamily && shadowCycleByFamily[productFamily] : undefined;
  const recommendedCycle = appliedRecommendedCycle || shadowRecommendedCycle;
  const availableCycles = availableCyclesFromPricingDetails(product == null ? void 0 : product.pricingDetails);
  const currentDefaultCycle = resolveCurrentDefaultCycle({
    hasSale: discountData == null ? void 0 : discountData.hasSale,
    alwaysDefaultsToYearly: isStickyPlanAndCycle ? !isUsGeo : isStickyButtonYearly && !(isUpgradeModel ? isUpgradeServing : isEligible)
  });
  const presetCycle = recommendedPurchaseCycle ? clampToAvailableCycle(recommendedPurchaseCycle, availableCycles) : currentDefaultCycle;
  const {
    lowestCycleAvailable,
    isLoading: isCycleAvailabilityLoading
  } = useCycleAvailability(selectedProductId || undefined);
  const isLoading = isDiscountLoading || upgrade.isLoading || (isUpgradeModel ? upgrade.isLoading : isRecommendationLoading) || isCycleAvailabilityLoading || isProductPending;
  const servedCycle = CycleUtils.maxCycle([lowestCycleAvailable, presetCycle]) || presetCycle;
  const fallbackReasons = React.useMemo(() => {
    if (isUpgradeModel) {
      if (recommendedPurchaseCycle || !(isUpgradeServing || upgrade.isShadow)) {
        return [];
      }
      if (upgrade.isTimeout) {
        return ['resolver_timeout'];
      }
      if (upgrade.isError) {
        return ['resolver_error'];
      }
      if (!isUpgradeServing) {
        return [];
      }
      return upgrade.isApplied ? ['no_cycle_for_family'] : ['no_response_data'];
    }
    if (!isEligible || recommendedPurchaseCycle) {
      return [];
    }
    const reasons = [];
    if (isTimeout) {
      reasons.push('resolver_timeout');
    } else if (isError) {
      reasons.push('resolver_error');
    }
    if (!isError) {
      if (!correlationId) {
        reasons.push('no_response_data');
      } else {
        reasons.push(productFamily ? 'suppressed_product' : 'unknown_product_family');
      }
    }
    return reasons;
  }, [isUpgradeModel, isUpgradeServing, upgrade.isShadow, upgrade.isTimeout, upgrade.isError, upgrade.isApplied, isEligible, recommendedPurchaseCycle, isTimeout, isError, correlationId, productFamily]);
  return {
    servedCycle,
    recommendedCycle: recommendedCycle || undefined,
    productFamily,
    availableCycles,
    applied: isUpgradeModel ? isUpgradeServing && Boolean(recommendedPurchaseCycle) : isEligible && Boolean(recommendedPurchaseCycle),
    fallbackReasons,
    ineligibleReasons: isUpgradeModel ? upgrade.ineligibleReasons : ineligibleReasons,
    correlationId: isUpgradeModel ? upgrade.requestId : correlationId,
    model: isUpgradeModel ? 'upgrade' : 'initial',
    upgradeState: isUpgradeModel ? upgradeStateOf(upgradeResponse) : undefined,
    isEligible: isUpgradeModel ? isUpgradeServing : isEligible,
    isShadow: isUpgradeModel ? upgrade.isShadow : isShadow,
    isShadowPending: isUpgradeModel ? upgrade.isShadowPending : isShadowPending,
    isLoading
  };
};
//# sourceMappingURL=useCycleDefaultDecision.js.map